// @flow

import React from 'react';
import { Image, Card, Feed } from 'semantic-ui-react';
import { getImageOrPlaceholder } from 'utils/resources';
import ProgressiveImage from 'shared/components/ProgressiveImage';
import ImageLoader from 'static/images/image-loader.svg';
import type { ThreadMessage, MessageAttachment } from '../../types';
import cx from 'classnames';

type Props = {
  message: ThreadMessage,
  isUnread: boolean,
  onDisplayMessage: (message: ThreadMessage, resource: BucketResource) => void,
};

const HOUR_FORMAT: string = 'HH:mm';

const ItemContent = ({ message, isUnread, onDisplayMessage }: Props) => {
  const contentAsHtml =
    message.content != null
      ? message.content.replace(/\r?\n/g, '<br />')
      : null;

  const displayMessage = (image: BucketResource) =>
    onDisplayMessage(message, image);

  const textClassName = cx('text', {
    unread: isUnread,
  });

  return (
    <div className="message-content">
      <div className="message-header">
        <strong>{message.user}</strong>
        <span className={isUnread ? 'unread' : ''}>
          {message.date.format(HOUR_FORMAT)}
        </span>
      </div>
      <p
        className={textClassName}
        dangerouslySetInnerHTML={{ __html: contentAsHtml }}
      />
      <ItemAttachments
        attachments={message.attachments}
        onDisplayImage={displayMessage}
      />
    </div>
  );
};

type AttachmentsProps = {
  attachments: Array<MessageAttachment>,
  onDisplayImage: (resource: BucketResource) => void,
};

const ItemAttachments = ({ attachments, onDisplayImage }: AttachmentsProps) => {
  if (attachments.length > 0) {
    const images = attachments.filter(
      a => a.resource.type.category === 'image'
    );
    const files = attachments.filter(a => a.resource.type.category !== 'image');

    return (
      <div className="image-content">
        <Image.Group size="small">
          {images.map(attachment => (
            <ImageAttachment
              key={attachment.id}
              attachment={attachment}
              onDisplay={onDisplayImage}
            />
          ))}
        </Image.Group>
        <Card.Group>
          {files.map(attachment => (
            <FileAttachment key={attachment.id} attachment={attachment} />
          ))}
        </Card.Group>
      </div>
    );
  } else {
    return null;
  }
};

type FileAttachmentProps = {
  attachment: MessageAttachment,
};

const FileAttachment = ({ attachment }: FileAttachmentProps) => {
  return (
    <Card href={attachment.resource.url.original}>
      <Card.Content>
        <Feed>
          <Feed.Event>
            <Feed.Label icon="file" />
            <Feed.Content>
              <Feed.Summary>{attachment.resource.name}</Feed.Summary>
            </Feed.Content>
          </Feed.Event>
        </Feed>
      </Card.Content>
    </Card>
  );
};

type ImageAttachmentProps = {
  attachment: MessageAttachment,
  onDisplay: (resource: BucketResource) => void,
};

const MAX_IMAGE_WIDTH: number = 150;

const ImageAttachment = ({ attachment, onDisplay }: ImageAttachmentProps) => {
  const url = getImageOrPlaceholder(attachment.resource, 'mdpi_1x');
  const { height, width } = attachment.resource.meta;
  const expectedHeight = height * MAX_IMAGE_WIDTH / width;

  const display = () => onDisplay(attachment.resource);

  return (
    <div className="ui small image" style={{ height: expectedHeight }}>
      <ProgressiveImage placeholder={ImageLoader} src={url}>
        {src => (
          <img
            src={src}
            crossOrigin="Anonymous"
            alt={attachment.resource.name}
            onClick={display}
            style={{ height: expectedHeight }}
          />
        )}
      </ProgressiveImage>
    </div>
  );
};

export default ItemContent;



// WEBPACK FOOTER //
// ./src/modules/messaging/components/messages/ItemContent.js